<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import BrandLogo from '@/components/common/BrandLogo.vue'
import LanguageSwitcher from '@/components/header/LanguageSwitcher.vue'
import UserMenu from '@/components/header/UserMenu.vue'
import AdminSidebar from '@/components/layout/AdminSidebar.vue'
import { useMaintenanceStore } from '@/stores/maintenance'
import { useUiStore } from '@/stores/ui'

/**
 * Shell of the admin panel.
 *
 * Its own header rather than the app one: group and server selectors mean
 * nothing here, and dropping them keeps the bar to a single row down to 349px
 * instead of needing the two-row treatment AppHeader uses.
 */
const { t } = useI18n()
const ui = useUiStore()
const route = useRoute()
const maintenance = useMaintenanceStore()

watch(
  () => route.fullPath,
  () => ui.closeMobileSidebar(),
)
</script>

<template>
  <div class="flex min-h-dvh flex-col">
    <a href="#main-content" class="sr-only-focusable btn-gold absolute top-2 left-2 z-100">
      {{ t('common.skipToContent') }}
    </a>

    <header class="chrome-surface border-gold-600/60 sticky top-0 z-40 border-b-2 shadow-raised">
      <div class="flex h-14 items-center gap-2 px-3 sm:gap-3 sm:px-4">
        <button
          type="button"
          class="chrome-item cursor-pointer px-2 lg:hidden"
          :aria-label="t('header.openMenu')"
          @click="ui.toggleMobileSidebar()"
        >
          <AppIcon name="mdi-menu" class="text-xl" />
        </button>

        <RouterLink :to="{ name: 'admin-home' }" class="flex min-w-0 shrink items-center gap-2">
          <!--
            Below sm the full wordmark plus the badge do not fit next to the
            hamburger, the language switcher and the avatar — they overlapped at
            390px. There the shield and the word "Admin" carry the same meaning
            in a third of the width.
          -->
          <span class="hidden items-center gap-2 sm:flex">
            <BrandLogo />
            <span class="badge bg-gold-500 text-abyss-900 shrink-0 uppercase">
              {{ t('common.roles.admin') }}
            </span>
          </span>
          <span class="flex items-center gap-2 sm:hidden">
            <AppIcon name="mdi-shield-crown-outline" class="text-gold-400 text-2xl" />
            <span class="font-display text-parchment-50 text-lg font-bold tracking-wide">
              {{ t('common.roles.admin') }}
            </span>
          </span>
        </RouterLink>

        <div class="ml-auto flex items-center gap-2">
          <!-- Reminder that the site is closed to everyone but us. -->
          <span
            v-if="maintenance.isActive"
            class="badge hidden bg-red-600 text-white sm:inline-flex"
          >
            <AppIcon name="mdi-wrench-outline" class="mr-1 text-sm" />
            {{ t('admin.maintenance.badge') }}
          </span>
          <LanguageSwitcher />
          <UserMenu />
        </div>
      </div>
    </header>

    <div class="flex flex-1">
      <!-- Desktop: docked, collapsible. -->
      <AdminSidebar class="hidden lg:flex" />

      <!--
        Mobile: off-canvas drawer. No transition, same reason as the app one —
        an enter transition that stalls (hidden tab starves rAF) would leave the
        drawer in the DOM at opacity 0, i.e. a menu that just does not open.
      -->
      <div v-if="ui.mobileSidebarOpen" class="fixed inset-0 z-50 flex lg:hidden">
        <div
          class="absolute inset-0 bg-black/60"
          role="presentation"
          @click="ui.closeMobileSidebar()"
        />
        <AdminSidebar class="relative h-full" force-expanded />
      </div>

      <main id="main-content" class="min-w-0 flex-1 p-4 sm:p-6">
        <slot />
      </main>
    </div>
  </div>
</template>
